import React from 'react'
import { Button, Card, Divider, message, Steps, Typography } from 'antd'
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { awsconfig, config } from 'configs'
import {
  billingActions,
  useBillingSelector,
} from 'store/billingSlice/billingReducer'
import { LeftOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { useAuthSelector } from 'store/authSlice/authReducer'
import { createBillinSubscription } from 'store/billingSlice/billingActions'
import { formatAmount } from 'utils'
import moment from 'moment'
import { Can } from 'permissions'

const WrappedReview = () => {
  const dispatch = useDispatch()
  const { user, organization } = useAuthSelector()
  const {
    loading: { creatingSubscription },
    tier,
  } = useBillingSelector()
  const stripe = useStripe()
  const elements = useElements()

  /* eslint-disable consistent-return */
  const onFinish = async (saveCard) => {
    dispatch(billingActions.setLoading({ creatingSubscription: true }))
    let cardToken = null
    if (saveCard) {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      })
      if (error) {
        message.error(error?.message || 'Invalid Card Information')
        dispatch(billingActions.setLoading({ creatingSubscription: false }))
        return false
      }
      cardToken = paymentMethod.id
    }

    const organizationId = organization?.id
    const payload = {
      user: {
        id: user?.id,
      },
      tierId: tier?.TierId,
    }
    if (cardToken) {
      payload.cardToken = cardToken
    }
    dispatch(createBillinSubscription(organizationId, payload))
  }

  const thirtyDaysFromNow = moment().add(31, 'days').format('D MMM YYYY')
  return (
    <Card bordered={false} style={{ minHeight: 'calc(100vh - 205px)' }}>
      <div className="t-flex t-items-center t-space-x-2 t-m-2.5 t-mb-6">
        <LeftOutlined
          style={{ fontSize: '1.3rem' }}
          className="t-cursor-pointer"
          onClick={() => dispatch(billingActions.setTier(null))}
        />
        <Typography className="t-font-roboto t-text-[1.5rem] md:t-text-[1.625rem] t-font-bold t-leading-[1.75rem] md:t-leading-[1.904375rem]">
          Review & subscribe
        </Typography>
      </div>
      <div className="t-grid t-grid-cols:1 md:t-grid-cols-10 t-gap-4 md:t-gap-8">
        <Card className="md:t-col-span-6 t-h-[200px]">
          <Typography className="t-text-roboto t-text-[22px] t-leading-[25.78px] t-font-semibold t-mb-3">
            Payment method
          </Typography>
          <Typography className="t-text-roboto t-text-[22px] t-leading-[25.78px] t-font-normal t-text-[rgba(0,0,0,0.6)]">
            Choose how you’d like to pay for extension subscription.
          </Typography>
          <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium t-mt-4 t-mb-2">
            Enter Card Number
          </Typography>
          <div className="t-p-2 t-rounded-md t-border t-border-grey-900">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '15px',
                    fontSmoothing: 'antialiased',
                  },
                },
              }}
            />
          </div>
        </Card>
        <Card className="md:t-col-span-4">
          <div className="t-flex t-justify-between t-px-[30px] t-p-2">
            <div>
              <Typography className="t-text-roboto t-text-[20px] t-leading-[27px] t-font-bold">
                {tier?.TierName}
              </Typography>
              <Typography className="t-text-roboto t-text-[20px] t-leading-[27px] t-font-medium t-text-[rgba(0,0,0,0.6)]">
                Monthly
              </Typography>
            </div>
            <div>
              <Typography className="t-text-roboto t-text-[22px] t-leading-[25.78px] t-font-bold">
                {formatAmount(tier?.BaseFee)}
              </Typography>
            </div>
          </div>
          <Divider />
          <div className="t-px-[30px]">
            <Steps
              progressDot
              current={0}
              direction="vertical"
              items={[
                {
                  description: (
                    <div className="t-flex t-justify-between">
                      <div>
                        <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                          Today
                        </Typography>
                        <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0,0,0,0.6)]">
                          Trial
                        </Typography>
                      </div>
                      <div className="t-flex t-flex-col t-items-end">
                        <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                          Free
                        </Typography>
                      </div>
                    </div>
                  ),
                },
                {
                  description: (
                    <div className="t-flex t-justify-between">
                      <div>
                        <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                          {thirtyDaysFromNow}
                        </Typography>
                        <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0,0,0,0.6)]">
                          {tier?.TierName}
                        </Typography>
                      </div>
                      <div className="t-flex t-flex-col t-items-end">
                        <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                          {formatAmount(tier?.BaseFee)}
                        </Typography>
                        <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0,0,0,0.6)] t-text-right">
                          Every 30 days
                        </Typography>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
          <Divider />
          <div className="t-flex t-flex-col t-gap-4 t-px-[30px]">
            <div className="t-flex t-justify-between">
              <div>
                <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                  Amount Due
                </Typography>
                <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0,0,0,0.6)]">
                  Today
                </Typography>
              </div>
              <div className="t-flex t-flex-col t-items-end">
                <Typography className="t-text-roboto t-text-4 t-leading-[18.75px] t-font-medium">
                  $0.00
                </Typography>
                <Typography className="t-text-roboto t-text-[13px] t-leading-[15.23px] t-font-normal t-text-[rgba(0,0,0,0.6)] t-text-right">
                  Plus application taxes
                </Typography>
              </div>
            </div>
            <Can
              I={config.actions.edit}
              a={config.components.organizationSubscription}
              passThrough
            >
              {(allowed) => (
                <>
                  <Button
                    type="primary"
                    size="large"
                    className="t-w-full t-h-[3rem] t-bg-[#1BA1E2] t-border-[#1BA1E2] t-text-[18px] t-leading-[22px]"
                    loading={creatingSubscription}
                    disabled={!allowed || !stripe || !elements}
                    onClick={() => onFinish(true)}
                  >
                    Subscribe
                  </Button>
                  <Button
                    type="default"
                    size="large"
                    className="t-w-full t-h-[3rem] t-border-[rgba(0, 0, 0, 0.6)] t-border-solid t-border-[2px] t-text-[18px] t-leading-[22px] t-text-[rgba(0,0,0,0.6)]"
                    loading={creatingSubscription}
                    disabled={!allowed}
                    onClick={() => onFinish(false)}
                  >
                    Skip for now
                  </Button>
                </>
              )}
            </Can>
            <Typography className="t-text-roboto t-text-[14px] t-leading-[16.41px] t-font-normal t-text-[rgba(0,0,0,0.6)]">
              Change or cancel your plan at anytime.
            </Typography>
          </div>
        </Card>
      </div>
    </Card>
  )
}

const StripeProvider = () => {
  const stripePromise = loadStripe(awsconfig.stripe_publish_key)
  return (
    <Elements stripe={stripePromise}>
      <WrappedReview />
    </Elements>
  )
}

const Review = StripeProvider
export { Review }
